/**
 * ModerationDialog — per-user moderation UI.
 * ===========================================
 *
 * Triggered from the user context menu when the actor has any moderation
 * permission in the active vault. Each action button calls the matching
 * /api/moderation/* endpoint with the user's bearer JWT.
 *
 * Permission gating is delegated to the engine — UI only renders buttons
 * the actor actually holds; the server re-checks via has_vault_permission
 * before any write (defense in depth).
 *
 * Layout: dark glass panel anchored at the click coordinates, with a
 * confirmation step for destructive actions (kick / ban / timeout).
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  PERMISSION_KICK_MEMBERS,
  PERMISSION_BAN_MEMBERS,
  PERMISSION_TIMEOUT_MEMBERS,
  PERMISSION_SERVER_MUTE,
  PERMISSION_SERVER_DEAFEN,
} from '../core/permissions/permissions';
import { TIMEOUT_PRESETS } from '../core/permissions/engine';
import { supabase } from '../supabase';

export type ModerationAction = 'KICK' | 'BAN' | 'TIMEOUT' | 'MUTE' | 'UNMUTE' | 'DEAFEN' | 'UNDEAFEN';

interface ModerationDialogProps {
  anchor: { x: number; y: number } | null;
  vaultId: string | null;
  targetId: string | null;
  targetName?: string;
  /** Bitmask of permissions the actor holds — drives button visibility. */
  actorPerms: {
    canKick: boolean;
    canBan: boolean;
    canTimeout: boolean;
    canMute: boolean;
    canDeafen: boolean;
  };
  onClose: () => void;
  onAfterAction?: (action: ModerationAction) => void;
}

async function callModeration(action: string, body: Record<string, unknown>) {
  const sess = (await supabase.auth.getSession()).data.session;
  const res = await fetch(`/api/moderation/${action}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(sess?.access_token
        ? { Authorization: `Bearer ${sess.access_token}` }
        : {}),
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const errBody: { error?: string } = await res.json().catch(() => ({}));
    throw new Error(errBody.error ?? `Failed (${res.status})`);
  }
  return res.json().catch(() => ({}));
}

export default function ModerationDialog({
  anchor, vaultId, targetId, targetName, actorPerms, onClose, onAfterAction,
}: ModerationDialogProps) {
  const [pendingAction, setPendingAction] = useState<ModerationAction | null>(null);
  const [banDuration, setBanDuration] = useState<number | 'permanent'>(24 * 60 * 60);
  const [timeoutSeconds, setTimeoutSeconds] = useState<number>(TIMEOUT_PRESETS[2].seconds);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!anchor || !vaultId || !targetId) return null;

  const execute = async (action: ModerationAction) => {
    setSubmitting(true);
    setError(null);
    try {
      if (action === 'KICK') {
        await callModeration('kick', { vaultId, targetId, reason });
      } else if (action === 'BAN') {
        await callModeration('ban', {
          vaultId, targetId, reason,
          durationSeconds: banDuration === 'permanent' ? null : banDuration,
        });
      } else if (action === 'TIMEOUT') {
        await callModeration('timeout', { vaultId, targetId, durationSeconds: timeoutSeconds, reason });
      } else if (action === 'MUTE') {
        await callModeration('mute', { vaultId, targetId });
      } else if (action === 'UNMUTE') {
        await callModeration('unmute', { vaultId, targetId });
      } else if (action === 'DEAFEN') {
        await callModeration('deafen', { vaultId, targetId });
      } else if (action === 'UNDEAFEN') {
        await callModeration('undeafen', { vaultId, targetId });
      }
      onAfterAction?.(action);
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Action failed');
    } finally {
      setSubmitting(false);
      setPendingAction(null);
    }
  };

  // Menu items rendered inline; selection flips into pendingAction mode for confirmation.
  return (
    <>
      <div
        className="fixed inset-0 z-[10000]"
        onClick={() => { if (!pendingAction) onClose(); }}
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        style={{ left: anchor.x, top: anchor.y }}
        className="absolute z-[10001] min-w-[260px] bg-[#0b0e1a]/95 backdrop-blur-2xl border border-white/10 rounded-2xl shadow-2xl p-2"
      >
        <div className="px-3 py-2 mb-1 border-b border-white/5">
          <div className="text-[9px] font-black text-white/30 uppercase tracking-[0.25em]">
            Moderate
          </div>
          <div className="text-xs font-bold text-white truncate max-w-[240px]">
            {targetName ?? targetId}
          </div>
        </div>

        <AnimatePresence mode="wait">
          {!pendingAction ? (
            <motion.div key="menu"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="flex flex-col gap-1"
            >
              {actorPerms.canKick && (
                <button onClick={() => setPendingAction('KICK')}
                  className="text-left px-3 py-2 rounded-xl text-xs text-amber-200 hover:bg-amber-500/10 font-bold">
                  Kick from Server
                </button>
              )}
              {actorPerms.canBan && (
                <button onClick={() => setPendingAction('BAN')}
                  className="text-left px-3 py-2 rounded-xl text-xs text-red-300 hover:bg-red-500/10 font-bold">
                  Ban Member
                </button>
              )}
              {actorPerms.canTimeout && (
                <button onClick={() => setPendingAction('TIMEOUT')}
                  className="text-left px-3 py-2 rounded-xl text-xs text-orange-200 hover:bg-orange-500/10 font-bold">
                  Timeout
                </button>
              )}
              {actorPerms.canMute && (
                <button onClick={() => setPendingAction('MUTE')}
                  className="text-left px-3 py-2 rounded-xl text-xs text-cyan-200 hover:bg-cyan-500/10 font-bold">
                  Server Mute
                </button>
              )}
              {actorPerms.canMute && (
                <button onClick={() => setPendingAction('UNMUTE')}
                  className="text-left px-3 py-2 rounded-xl text-xs text-cyan-200/60 hover:bg-cyan-500/10 font-bold">
                  Server Unmute
                </button>
              )}
              {actorPerms.canDeafen && (
                <button onClick={() => setPendingAction('DEAFEN')}
                  className="text-left px-3 py-2 rounded-xl text-xs text-purple-200 hover:bg-purple-500/10 font-bold">
                  Server Deafen
                </button>
              )}
              {actorPerms.canDeafen && (
                <button onClick={() => setPendingAction('UNDEAFEN')}
                  className="text-left px-3 py-2 rounded-xl text-xs text-purple-200/60 hover:bg-purple-500/10 font-bold">
                  Server Undeafen
                </button>
              )}
              {!actorPerms.canKick && !actorPerms.canBan && !actorPerms.canTimeout
                && !actorPerms.canMute && !actorPerms.canDeafen && (
                <div className="px-3 py-3 text-[10px] text-white/40 uppercase tracking-widest">
                  You have no moderation powers here.
                </div>
              )}
            </motion.div>
          ) : (
            <motion.div key="confirm"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="p-3 flex flex-col gap-2"
            >
              {pendingAction === 'BAN' && (
                <div className="flex flex-col gap-1.5">
                  <label className="text-[9px] font-black text-white/40 uppercase tracking-widest">Duration</label>
                  <select
                    value={banDuration === 'permanent' ? 'permanent' : String(banDuration)}
                    onChange={e => {
                      const v = e.target.value;
                      setBanDuration(v === 'permanent' ? 'permanent' : parseInt(v, 10));
                    }}
                    className="bg-black/40 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white"
                  >
                    <option value="3600">1 hour</option>
                    <option value={(24 * 60 * 60).toString()}>1 day</option>
                    <option value={(7 * 24 * 60 * 60).toString()}>1 week</option>
                    <option value="permanent">Permanent</option>
                  </select>
                </div>
              )}
              {pendingAction === 'TIMEOUT' && (
                <div className="flex flex-col gap-1.5">
                  <label className="text-[9px] font-black text-white/40 uppercase tracking-widest">Duration</label>
                  <div className="grid grid-cols-2 gap-1">
                    {TIMEOUT_PRESETS.map(p => (
                      <button key={p.seconds}
                        onClick={() => setTimeoutSeconds(p.seconds)}
                        className={`px-2 py-1.5 rounded-lg text-[10px] font-bold border ${
                          timeoutSeconds === p.seconds
                            ? 'border-[var(--neon)]/60 bg-[var(--neon)]/10 text-[var(--neon)]'
                            : 'border-white/10 bg-white/5 text-white/60 hover:bg-white/10'
                        }`}>
                        {p.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex flex-col gap-1">
                <label className="text-[9px] font-black text-white/40 uppercase tracking-widest">
                  Reason (optional)
                </label>
                <input
                  type="text"
                  maxLength={200}
                  value={reason}
                  onChange={e => setReason(e.target.value)}
                  className="bg-black/40 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white"
                  placeholder="Visible in the audit log"
                />
              </div>
              {error && (
                <div className="text-[10px] text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-2 py-1.5">
                  {error}
                </div>
              )}
              <div className="flex gap-2 mt-1">
                <button
                  onClick={() => setPendingAction(null)}
                  disabled={submitting}
                  className="flex-1 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest text-white/60 hover:bg-white/5"
                >Back</button>
                <button
                  onClick={() => execute(pendingAction)}
                  disabled={submitting}
                  className="flex-1 px-3 py-1.5 rounded-lg bg-red-500/20 border border-red-500/50 text-red-200 hover:bg-red-500/30 text-[10px] font-black uppercase tracking-widest disabled:opacity-40"
                >
                  {submitting ? 'Working…' : 'Confirm'}
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </>
  );
}
